import { startOfWeek, addDays, addWeeks, subWeeks, format, isToday as isTodayFns, getISOWeek } from 'date-fns';

export const WEEKDAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const GRID_START_MINUTES = 0; // 00:00
export const GRID_END_MINUTES = 24 * 60; // 24:00 — the planner covers the full day
export const PIXELS_PER_MINUTE = 1;
export const DEFAULT_SCROLL_MINUTES = 9 * 60; // scroll to 09:00 by default — most staff plan the 8/9-to-5/6 workday

export const DAILY_TARGET_MINUTES = 8 * 60; // 8h/day
export const WEEKLY_TARGET_MINUTES = 40 * 60; // 40h/week

// Types that count as break time on the summary card.
export const BREAK_TYPES = ['Break', 'Lunch'];
// Types that don't count toward planned/completed *work* hours at all (break time plus personal time like Sleep).
export const NON_WORK_TYPES = ['Break', 'Lunch', 'Sleep'];

export const getWeekStart = (date = new Date()) => startOfWeek(date, { weekStartsOn: 1 });

export const getWeekKey = (weekStart) => format(weekStart, 'yyyy-MM-dd');

// Monday through Saturday — Sunday is always treated as a holiday and never included.
export const getWeekDates = (weekStart) => Array.from({ length: 6 }, (_, i) => addDays(weekStart, i));

export const getDayKey = (date) => format(date, 'yyyy-MM-dd');

/** "YYYY-MM" — the backend's R2 sort key. Accepts a Date or a "YYYY-MM-DD" day key. */
export const getMonthKey = (dateOrDayKey) => {
  const date = dateOrDayKey instanceof Date ? dateOrDayKey : new Date(`${dateOrDayKey}T00:00:00`);
  return format(date, 'yyyy-MM');
};

export const shiftDayKey = (dayKey, days) => {
  const d = new Date(`${dayKey}T00:00:00`);
  return getDayKey(addDays(d, days));
};

export const goToNextWeek = (weekStart) => addWeeks(weekStart, 1);
export const goToPreviousWeek = (weekStart) => subWeeks(weekStart, 1);

export const getWeekNumber = (weekStart) => getISOWeek(weekStart);

export const formatWeekRange = (weekStart) => {
  const weekEnd = addDays(weekStart, 5);
  const sameMonth = format(weekStart, 'MMM') === format(weekEnd, 'MMM');
  const startLabel = format(weekStart, sameMonth ? 'dd' : 'dd MMM');
  const endLabel = format(weekEnd, 'dd MMM yyyy');
  return `${startLabel} – ${endLabel}`;
};

export const isDateToday = (date) => isTodayFns(date);

export const minutesToTimeLabel = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const dateFromDayAndMinutes = (dayKey, minutes) => {
  const d = new Date(`${dayKey}T00:00:00`);
  // Cap at 23:59 — setHours(24, ...) would silently roll over to the next day.
  const capped = Math.min(minutes, 23 * 60 + 59);
  d.setHours(Math.floor(capped / 60), capped % 60, 0, 0);
  return d;
};

export const minutesFromDate = (date) => date.getHours() * 60 + date.getMinutes();

export const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '0h';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const snapToInterval = (minutes, intervalMinutes) =>
  Math.round(minutes / intervalMinutes) * intervalMinutes;

const isWorkActivity = (activity) => !NON_WORK_TYPES.includes(activity.type);
const isBreakActivity = (activity) => BREAK_TYPES.includes(activity.type);

export const calculateSummary = (activities) => {
  const now = new Date();

  let plannedWorkMinutes = 0;
  let plannedBreakMinutes = 0;
  let completedWorkMinutes = 0;

  activities.forEach((activity) => {
    const duration = activity.endMinutes - activity.startMinutes;
    if (isWorkActivity(activity)) {
      plannedWorkMinutes += duration;
      const activityEnd = dateFromDayAndMinutes(activity.day, activity.endMinutes);
      if (activityEnd <= now) {
        completedWorkMinutes += duration;
      }
    } else if (isBreakActivity(activity)) {
      plannedBreakMinutes += duration;
    }
  });

  const remainingWorkMinutes = Math.max(plannedWorkMinutes - completedWorkMinutes, 0);

  return {
    plannedWorkMinutes,
    plannedBreakMinutes,
    completedWorkMinutes,
    remainingWorkMinutes,
  };
};

const activitiesOverlap = (a, b) => a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes;

export const getValidationWarnings = (activities, weekDates) => {
  const warnings = [];

  weekDates.forEach((date, index) => {
    const dayKey = getDayKey(date);
    const dayLabel = WEEKDAY_LABELS[index];
    const dayActivities = activities.filter((a) => a.day === dayKey);

    const workMinutes = dayActivities
      .filter(isWorkActivity)
      .reduce((sum, a) => sum + (a.endMinutes - a.startMinutes), 0);

    if (workMinutes > DAILY_TARGET_MINUTES) {
      warnings.push({
        id: `over-${dayKey}`,
        severity: 'warning',
        message: `Your ${dayLabel} plan contains ${formatDuration(workMinutes)} of planned work.`,
      });
    }

    const sorted = [...dayActivities].sort((a, b) => a.startMinutes - b.startMinutes);
    let hasOverlap = false;
    for (let i = 1; i < sorted.length; i += 1) {
      if (activitiesOverlap(sorted[i - 1], sorted[i])) {
        hasOverlap = true;
        break;
      }
    }
    if (hasOverlap) {
      warnings.push({
        id: `overlap-${dayKey}`,
        severity: 'error',
        message: `Your ${dayLabel} schedule has an overlapping activity.`,
      });
    }
  });

  const totalWorkMinutes = activities
    .filter(isWorkActivity)
    .reduce((sum, a) => sum + (a.endMinutes - a.startMinutes), 0);
  const remaining = WEEKLY_TARGET_MINUTES - totalWorkMinutes;

  if (remaining > 0) {
    warnings.push({
      id: 'week-remaining',
      severity: 'info',
      message: `You have ${formatDuration(remaining)} of unplanned working time remaining.`,
    });
  } else if (remaining < 0) {
    warnings.push({
      id: 'week-over',
      severity: 'warning',
      message: `You have planned ${formatDuration(-remaining)} more than your standard ${formatDuration(
        WEEKLY_TARGET_MINUTES
      )} working week.`,
    });
  }

  return warnings;
};
